import { Input } from '@components/core-ui/form-elements/Input';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import SearchIcon from '@components/icons/Search.svg';
import TrainingIcon from '@components/icons/Training.svg';
import { cn } from '@helpers/cn';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { useMediaQuery } from 'app/hooks/useMediaQuery';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import React, { Fragment, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AccountActions } from './components/AccountActions';
import { ActionButtonContent } from './components/ActionButtonContent';
import { BackButton } from './components/BackButton';
import { Filter } from './components/Filter';
import { HowToUse } from './components/HowToUse';
import { OverflowMenu } from './components/OverflowMenu';
import { TOverflowMenuItem } from './components/OverflowMenu/types/TOverflowMenuItem';
import { Search } from './components/Search';
import { SearchToggle } from './components/SearchToggle';
import { Title } from './components/Title';
import { WatchTraining } from './components/WatchTraining';
import { AccountActionsPlacementContext } from './contexts/AccountActionsPlacementContext';
import { shouldShowHowToUse } from './helpers/shouldShowHowToUse';
import { shouldShowTrainingLink } from './helpers/shouldShowTrainingLink';
import { THeaderAction } from './types/THeaderAction';
import { TProps } from './types/TProps';

const Divider = () => <div className="hidden h-[35px] w-px shrink-0 bg-[var(--color-divider)] xl:block" />;

const toMenuItem = (action: THeaderAction): TOverflowMenuItem => ({
  key: action.key,
  label: action.label,
  icon: action.icon,
  onClick: action.onClick,
  disabled: action.disabled || action.loading,
});

export const Header = ({
  title,
  backRoute,
  setSearch,
  search = '',
  isSearchDisabled,
  globalSearch,
  primaryAction,
  actions = [],
  buttonLabel,
  buttonIcon,
  buttonOnClick,
  buttonDisabled = false,
  buttonLoading = false,
  secondButtonLabel,
  secondButtonIcon,
  secondButtonOnClick,
  onFilterClick,
  filterCounter = 0,
  btnRef,
  leftSideContent,
}: TProps) => {
  const { me } = useAuthContext();
  const { openModal } = useModalContext();
  const desktop = useMediaQuery('(min-width: 1280px)');
  const tablet = useMediaQuery('(min-width: 768px)');
  const accountActionsPlacement = useContext(AccountActionsPlacementContext);
  const location = useLocation();
  const pathname = location?.pathname ?? '';
  const locationState = location?.state as {
    from?: string;
  };
  const [searchExpanded, setSearchExpanded] = useState(Boolean(search));

  if (!me || !me.name) {
    return null;
  }

  const utilityActions: THeaderAction[] = [];
  if (buttonLabel) {
    utilityActions.push({
      key: 'utility-primary',
      label: buttonLabel,
      icon: buttonIcon,
      onClick: () => buttonOnClick?.(),
      disabled: buttonDisabled,
      loading: buttonLoading,
    });
  }
  if (secondButtonLabel) {
    utilityActions.push({
      key: 'utility-secondary',
      label: secondButtonLabel,
      icon: secondButtonIcon,
      onClick: () => secondButtonOnClick?.(),
    });
  }

  const inlineSecondaryActions = tablet ? actions : [];
  const inlineUtilityActions = desktop ? utilityActions : [];
  const hasLeftSideContent = Boolean(leftSideContent?.length);
  const hasControls =
    Boolean(primaryAction) ||
    inlineSecondaryActions.length > 0 ||
    hasLeftSideContent ||
    inlineUtilityActions.length > 0;
  const showsMobileAccountActions = !desktop && accountActionsPlacement === 'header';
  const showsPhoneSearchRow = Boolean(setSearch) && !tablet && searchExpanded;

  const overflowItems: TOverflowMenuItem[] = [
    ...(tablet ? [] : actions).map(toMenuItem),
    ...(desktop ? [] : utilityActions).map(toMenuItem),
  ];
  if (!desktop && shouldShowHowToUse(pathname)) {
    overflowItems.push({
      key: 'how-to-use',
      label: 'How To Use',
      icon: TrainingIcon,
      onClick: () => openModal(ModalNamesEnum.AutoScriptOneTutorialVideo),
    });
  }
  if (!desktop && shouldShowTrainingLink(pathname)) {
    overflowItems.push({ key: 'training', label: 'Training Videos', icon: TrainingIcon, to: routes.training.make() });
  }

  const toggleSearch = () => {
    if (searchExpanded && search) {
      setSearch?.('');
    }
    setSearchExpanded((expanded) => !expanded);
  };

  const searchInput = setSearch ? (
    <Input
      icon={SearchIcon}
      name="search"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      label="Search"
      value={search}
      disabled={isSearchDisabled ?? false}
      autoFocus={!tablet}
    />
  ) : null;

  return (
    <header className="relative z-[19] w-full min-w-0 bg-surface shadow-[0_1px_6px_var(--color-shadow)] xl:sticky xl:top-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 px-4 py-3 md:gap-x-4 md:px-[30px] xl:min-h-24 xl:gap-[25px] xl:py-2.5">
        {backRoute || title ? (
          <div className="order-1 flex shrink-0 items-center gap-2 xl:gap-[25px]">
            <BackButton backRoute={locationState?.from ? locationState.from : backRoute} />
            <Title title={title} />
          </div>
        ) : null}

        {hasControls ? (
          <div className="order-4 flex min-w-0 flex-wrap items-center gap-2.5 max-md:basis-full md:order-2 xl:gap-[25px]">
            {title ? <Divider /> : null}
            {primaryAction ? (
              <Button
                data-testid={primaryAction.testId}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled || primaryAction.loading}
                aria-busy={primaryAction.loading ?? false}
              >
                <ActionButtonContent action={primaryAction} />
              </Button>
            ) : null}
            {inlineSecondaryActions.map((action) => (
              <Button
                key={action.key}
                data-testid={action.testId}
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                aria-busy={action.loading ?? false}
              >
                <ActionButtonContent action={action} />
              </Button>
            ))}
            {leftSideContent ? leftSideContent.map((item, index) => <Fragment key={index}>{item}</Fragment>) : null}
            {inlineUtilityActions.length ? (
              <>
                {primaryAction || inlineSecondaryActions.length || hasLeftSideContent ? <Divider /> : null}
                <div className="relative flex min-w-0 flex-wrap items-center gap-2.5" ref={btnRef}>
                  {inlineUtilityActions.map((action) => (
                    <BoldButton
                      key={action.key}
                      onClick={action.onClick}
                      disabled={action.disabled || action.loading}
                      aria-busy={action.loading ?? false}
                      className="!w-auto"
                    >
                      <ActionButtonContent action={action} />
                    </BoldButton>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        <div
          className={cn(
            'order-2 ml-auto flex shrink-0 items-center gap-1 md:order-3 md:gap-3 xl:gap-[25px]',
            showsMobileAccountActions && 'max-md:gap-0',
          )}
        >
          {desktop ? (
            <>
              <HowToUse />
              <WatchTraining />
            </>
          ) : null}
          <Filter onClick={onFilterClick} filterCounter={filterCounter} />
          {setSearch ? (
            tablet ? (
              <div className="w-[220px] xl:w-[200px]">{searchInput}</div>
            ) : (
              <SearchToggle expanded={searchExpanded} active={Boolean(search)} onClick={toggleSearch} />
            )
          ) : null}
          {globalSearch ? <Search /> : null}
          <OverflowMenu items={overflowItems} />
          {showsMobileAccountActions || desktop ? <AccountActions /> : null}
        </div>

        {showsPhoneSearchRow ? <div className="order-3 basis-full">{searchInput}</div> : null}
      </div>
    </header>
  );
};
